import React, { useEffect, useState } from "react";
import { useDropzone } from "react-dropzone";
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import CloudStorage from "../../../../../CloudStorage/CloudStorage";
import { urtoast } from "../../../../Layouts/CustomPopup/eventEmitter";
import { v4 as uuidv4 } from "uuid";
import DisplayFileProper from "../../../../Common/FilesRelevent/DisplayFileProper";
import DownloadButton from "../../../../Common/FilesRelevent/DownloadButton";
import AsteriskLabel from "../../../../Common/AsteriskLabel";
import { validImageTypes, validDocumentTypes, MAX_FILE_LIMIT } from "../../../../../assests/extention/fileextention";
import useFileValidator from "../../../../../hooks/useFileValidator";
import UploadLoader from "../../../../Common/Loader/UploadLoader";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/jpg"];
const ALLOWED_DOC_TYPES = ["application/pdf"];
// Will be extended if allowAllDocs is true

export default function CompanyFileUpload({
  formik,
  doctype,
  name = "files",
  title = "",
  type = "multiple", // "single" or "multiple"
  allowAllDocs = false, // Added prop
}) {
  const [selectedFiles, setSelectedFiles] = useState(
    type == "single" ? null : []
  );
  const { validating, validateFiles } = useFileValidator();

  // Sync formik values with local state
  useEffect(() => {
    const value = formik?.values[name];
    const newValue = type == "single" ? value || null : value || [];
    setSelectedFiles((prev) => JSON.stringify(prev) !== JSON.stringify(newValue) ? newValue : prev);
  }, [formik?.values[name], type]);

  // 🔼 Handle file uploads
  const handleUpload = async (files) => {
    console.log("handleUpload started with files:", files);
    const activeCount =
      type == "single"
        ? selectedFiles
          ? 1
          : 0
        : (selectedFiles || []).filter((f) => f.status != 3).length;

    if (type !== "single" && activeCount + files.length > MAX_FILE_LIMIT) {
      urtoast(`You can upload maximum ${MAX_FILE_LIMIT} files.`, 2);
      return;
    }

    // Front-end Local Validation before hitting API
    const hasLargeFile = files.some(file => file.size > 10 * 1024 * 1024);
    if (hasLargeFile) {
      urtoast("File size exceeds 10 MB limit.", 2);
      return;
    }

    const currentAllowedDocs = allowAllDocs ? [...ALLOWED_DOC_TYPES, ...validDocumentTypes] : ALLOWED_DOC_TYPES;

    const hasInvalidType = files.some(file => {
      if (doctype == 1) return false;
      return !ALLOWED_IMAGE_TYPES.includes(file.type) && !currentAllowedDocs.includes(file.type);
    });

    if (hasInvalidType) {
      const msg = allowAllDocs 
        ? "Unsupported file format. Please upload PDF, DOCX, XLSX, PPTX, ZIP, JPG, JPEG, or PNG."
        : "Unsupported file format. Please upload PDF, JPG, JPEG, or PNG.";
      urtoast(msg, 2);
      return;
    }

    // 🛡️ Validate all files via backend API
    const isValid = await validateFiles(files);
    if (!isValid) return;

    const uploadPromises = files.map((file) => {
      const uniqueId = uuidv4();
      const storageRef = ref(getStorage(), `/Category/${uniqueId}_${file.name}`);
      const uploadTask = uploadBytesResumable(storageRef, file);

      return uploadTask
        .then(async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          return url;
        })
        .catch((error) => {
          urtoast(`Error uploading ${file.name}: ${error.message}`, 2);
          throw error;
        });
    });

    Promise.all(uploadPromises)
      .then((uploadedUrls) => {
        if (type == "single") {
          const uploadedUrl = uploadedUrls[0];
          setSelectedFiles(uploadedUrl);
          formik.setFieldValue(name, uploadedUrl);
        } else {
          const updatedFiles = [
            ...(selectedFiles || []),
            ...uploadedUrls.map((url) => ({
              id: "",
              file: url,
              name: "",
              status: 1,
            })),
          ];
          setSelectedFiles(updatedFiles);
          formik.setFieldValue(name, updatedFiles);
        }
      })
      .catch((err) => console.error("Upload error:", err));
  };

  // Dropzone config
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleUpload,
    multiple: type != "single",
    accept: doctype == 1
      ? undefined
      : allowAllDocs
        ? {
          'image/*': ['.jpeg', '.jpg', '.png'],
          'application/pdf': ['.pdf'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
          'application/zip': ['.zip'],
          'application/x-zip-compressed': ['.zip'],
          'application/msword': ['.doc'],
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.ms-powerpoint': ['.ppt'],
        }
        : {
          'image/*': ['.jpeg', '.jpg', '.png'],
          'application/pdf': ['.pdf']
        }
  });

  // 🗑️ FIXED: Delete handler with status 3 for backend
  const handleDeleteImage = (index) => {
    if (type == "single") {
      setSelectedFiles(null);
      formik.setFieldValue(name, null);
    } else {
      // Get the file from activeFiles to find it in selectedFiles
      const fileToDelete = activeFiles[index];

      // Mark the file with status 3 in selectedFiles array
      const updatedFiles = selectedFiles.map(item =>
        item.file === fileToDelete.file ? { ...item, status: 3 } : item
      );

      setSelectedFiles(updatedFiles);
      formik.setFieldValue(name, updatedFiles);
    }
  };

  // Active (non-deleted) files for UI
  const activeFiles =
    type == "single"
      ? selectedFiles
        ? [selectedFiles]
        : []
      : (selectedFiles || []).filter((item) => item.status !== 3);

  return (
    <div className="add-portfolio position-relative">
      {validating && <UploadLoader />}
      <div className="col-md-12 ">
        <div className="row justify-content-between" style={{ rowGap: '20px' }}>
          {activeFiles.length == 0 ? (
            <div className="col-md-5">
              <div className="image-container">
                <img
                  src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/file-upload.png`}
                  className="cover-img w-100"
                  alt="Upload Placeholder"
                />
              </div>
            </div>

          ) : (
            activeFiles.map((item, index) => (
              <div className="col-md-6 " key={index}>
                <div className="image-container position-relative">
                  <DisplayFileProper
                    item={type == "single" ? { file: item } : item}
                  />
                  {/* <DownloadButton
                file={type == "single" ? { file: item } : item}
              /> */}
                  <button
                    type="button"
                    className="delete-button position-absolute top-0 end-0 m-1"
                    onClick={() => handleDeleteImage(index)}
                  >
                    <img
                      src={`${CloudStorage.cloudurl}/Assets/Images/expertregisteration/delete-icon.svg`}
                      alt="Delete"
                    />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      <div {...getRootProps({ className: "dropzone borderstylex mt-3" })}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the file here...</p>
        ) : (
          <p>
            Drag and drop or <span>Browse file</span>
          </p>
        )}
      </div>

      {doctype == 1 ? (
        ""
      ) : (
        <ul className="border-ul mt-2">
          <li>Images (JPG, PNG, JPEG up to 10MB)</li>
          <li>Documents ({allowAllDocs ? "PDF, DOCX, XLSX, PPTX, ZIP" : "PDF"} up to 10MB)</li>
        </ul>
      )}
    </div>
  );
}